import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    AssistantRuntimeProvider,
    ComposerPrimitive,
    ThreadPrimitive,
    useLocalRuntime,
    useAssistantState
} from "@assistant-ui/react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { getAiChatStreamResponse } from "../../../services/aiChatService";
import { searchHasOfferUsers } from "../../../services/usersService";
import styles from './AiChatAssistantMobile.module.scss';
import SendIcon from '@mui/icons-material/Send';

// ─── Loading indicator ────────────────────────────────────────────────────────
const AssistantLoadingStatus = () => {
    const isRunning = useAssistantState((s) => s.thread.isRunning);
    const messages = useAssistantState((s) => s.thread.messages);
    const lastMessage = messages[messages.length - 1];
    const isStreaming = lastMessage?.role === 'assistant' &&
        (lastMessage.content?.length > 0 || lastMessage.text?.length > 0);
    if (!isRunning || isStreaming) return null;
    return (
        <Box className={styles.assistantBubble}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <CircularProgress size={14} thickness={5} sx={{ color: '#1976d2' }} />
            <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '13px' }}>
                Thinking…
            </Typography>
        </Box>
    );
};

// ─── Message formatter ────────────────────────────────────────────────────────
const formatMessageContent = (text, role) => {
    if (!text) return text;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => {
        if (part.match(urlRegex)) {
            const isExcel = part.includes('.xlsx') || part.includes('.xls');
            return (
                <a key={`link-${i}`} href={part} target="_blank" rel="noopener noreferrer"
                    style={{
                        color: role === 'user' ? '#fff' : '#1976d2',
                        textDecoration: 'underline', fontWeight: '600',
                        display: isExcel ? 'block' : 'inline',
                        marginTop: isExcel ? '8px' : '0'
                    }}>
                    {isExcel ? '📊 Download Report' : part}
                </a>
            );
        }
        return part.split(/(\*\*[^*]+\*\*)/g).map((bp, bi) =>
            bp.startsWith('**') && bp.endsWith('**')
                ? <strong key={`bold-${i}-${bi}`}>{bp.slice(2, -2)}</strong>
                : <span key={`text-${i}-${bi}`}>{bp}</span>
        );
    });
};

// ─── Single message bubble ────────────────────────────────────────────────────
const ChatMessage = ({ message }) => {
    const text = typeof message.content === 'string'
        ? message.content
        : Array.isArray(message.content)
            ? message.content.map(c => c.text || '').join('')
            : (message.text || '');
    if (!text && message.role === 'assistant') return null;
    return (
        <div className={message.role === 'user' ? styles.userBubble : styles.assistantBubble}>
            <Typography variant="body1"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.95rem' }}>
                {formatMessageContent(text, message.role)}
            </Typography>
        </div>
    );
};

// ─── Inner thread UI (must live *inside* AssistantRuntimeProvider) ────────────
const ChatThreadUI = ({ conversationId, inputValue, setInputValue, showSuggestions, setShowSuggestions, filteredItems, setFilteredItems, triggerChar, setTriggerChar, query, setQuery, offers }) => {
    const viewportRef = useRef(null);
    const messages = useAssistantState((s) => s.thread.messages);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (viewportRef.current) {
            viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
        }
    }, [messages]);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);
        const cursor = e.target.selectionStart;
        const before = val.slice(0, cursor);
        const lastHash = before.lastIndexOf('#');
        const lastAt = before.lastIndexOf('@');
        const lastTriggerIdx = Math.max(lastHash, lastAt);

        if (lastTriggerIdx !== -1) {
            const char = before[lastTriggerIdx];
            const afterTrigger = before.slice(lastTriggerIdx + 1);
            if (char === '@' || !afterTrigger.includes(' ')) {
                setQuery(afterTrigger);
                setTriggerChar(char);
                if (char === '#') setShowSuggestions(true);
                return;
            }
        }
        setShowSuggestions(false);
        setTriggerChar(null);
    };

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (triggerChar === '#') {
                const filtered = offers.filter(o =>
                    (o.name || '').toLowerCase().includes(query.toLowerCase()) ||
                    String(o.offerId).includes(query)
                );
                setFilteredItems(filtered.slice(0, 10));
                setShowSuggestions(filtered.length > 0);
            } else if (triggerChar === '@' && query.length >= 2) {
                try {
                    const res = await searchHasOfferUsers({ query });
                    const data = res.data || [];
                    setFilteredItems(data);
                    setShowSuggestions(data.length > 0);
                } catch { setShowSuggestions(false); }
            }
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, triggerChar, offers]);

    const handleSelectItem = (item) => {
        const lastTrigger = inputValue.lastIndexOf(triggerChar);
        const prefix = inputValue.slice(0, lastTrigger);
        const inserted = triggerChar === '#'
            ? `#${item.name}-${item.offerId} `
            : `@${item.fullname}-${item.hasoffersid} `;
        setInputValue(prefix + inserted);
        setShowSuggestions(false);
    };

    return (
        <ThreadPrimitive.Root style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <ThreadPrimitive.Viewport
                ref={viewportRef}
                className={styles.messageList}
                style={{ flex: 1, overflowY: 'auto' }}>
                <ThreadPrimitive.Empty>
                    <div className={styles.emptyState}>
                        <div className={styles.icon}>✨</div>
                        <Typography variant="h2">InfluioGPT Mobile</Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Ask anything about your campaigns, influencers, or performance.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 280 }}>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                p: '10px 14px', borderRadius: '12px',
                                background: 'rgba(25, 118, 210, 0.06)',
                                border: '1px solid rgba(25, 118, 210, 0.12)'
                            }}>
                                <Box sx={{
                                    background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                                    color: 'white', px: 1, py: 0.25,
                                    borderRadius: '6px', fontWeight: 800,
                                    fontSize: '1rem', lineHeight: 1.5,
                                    minWidth: 28, textAlign: 'center'
                                }}>@</Box>
                                <Typography variant="body2" sx={{ color: '#444', fontWeight: 500 }}>
                                    Mention an influencer
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                p: '10px 14px', borderRadius: '12px',
                                background: 'rgba(156, 39, 176, 0.06)',
                                border: '1px solid rgba(156, 39, 176, 0.12)'
                            }}>
                                <Box sx={{
                                    background: 'linear-gradient(135deg, #9c27b0, #7b1fa2)',
                                    color: 'white', px: 1, py: 0.25,
                                    borderRadius: '6px', fontWeight: 800,
                                    fontSize: '1rem', lineHeight: 1.5,
                                    minWidth: 28, textAlign: 'center'
                                }}>#</Box>
                                <Typography variant="body2" sx={{ color: '#444', fontWeight: 500 }}>
                                    Reference an offer
                                </Typography>
                            </Box>
                        </Box>
                    </div>
                </ThreadPrimitive.Empty>

                {messages.map(msg => <ChatMessage key={msg.id} message={msg} />)}
                <AssistantLoadingStatus />
            </ThreadPrimitive.Viewport>

            {showSuggestions && filteredItems.length > 0 && (
                <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
                    <Paper sx={{
                        position: 'absolute', bottom: '80px', left: '10px', right: '10px',
                        maxHeight: '200px', overflowY: 'auto', zIndex: 1000, borderRadius: '16px'
                    }}>
                        <List dense>
                            {filteredItems.map(item => (
                                <ListItemButton
                                    key={triggerChar === '#' ? item.offerId : item.hasoffersid}
                                    onClick={() => handleSelectItem(item)}>
                                    <ListItemText
                                        primary={triggerChar === '#' ? item.name : item.fullname}
                                        secondary={`ID: ${triggerChar === '#' ? item.offerId : item.hasoffersid}`} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </ClickAwayListener>
            )}

            <div className={styles.inputArea}>
                <div className={styles.inputWrapper}>
                    <ComposerPrimitive.Input
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="Type a message…"
                        autoComplete="off"
                        maxRows={4}
                        style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            padding: '12px 0',
                            fontSize: '1rem',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            outline: 'none',
                            color: '#1a1a1a',
                            resize: 'none',
                            lineHeight: '1.5',
                            display: 'block',
                        }}
                    />
                </div>
                <ComposerPrimitive.Send className={styles.sendButton}>
                    <SendIcon fontSize="small" />
                </ComposerPrimitive.Send>
            </div>
        </ThreadPrimitive.Root>
    );
};

// ─── Outer component – owns the runtime, renders provider + UI ────────────────
const ActiveChatMobileSession = ({ conversationId }) => {
    const offers = useSelector((state) => state.offersList?.offers || []);

    const [inputValue, setInputValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);
    const [triggerChar, setTriggerChar] = useState(null);
    const [query, setQuery] = useState("");

    const runtime = useLocalRuntime({
        async *run({ messages }) {
            setInputValue("");
            const userMessage = messages[messages.length - 1];
            try {
                const response = await getAiChatStreamResponse(conversationId, messages);
                if (!response.ok) throw new Error("API Error");
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let accumulatedText = "";
                let buffer = "";
                const responseId = `assistant-${Date.now()}`;

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop();

                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed || !trimmed.startsWith('data:')) continue;
                        const data = trimmed.substring(5).trim();
                        if (data === '[DONE]') continue;
                        try {
                            const json = JSON.parse(data);
                            const chunk = json.content || json.text || '';
                            if (chunk) {
                                accumulatedText += chunk.replace(/\\n/g, '\n');
                                yield {
                                    id: responseId,
                                    parentId: userMessage.id,
                                    role: "assistant",
                                    content: [{ type: "text", text: accumulatedText }]
                                };
                            }
                        } catch { /* ignore parse errors */ }
                    }
                }
            } catch {
                yield {
                    id: `err-${Date.now()}`, role: "assistant",
                    content: [{ type: "text", text: "Connection error. Please try again." }]
                };
            }
        },
    });

    return (
        <AssistantRuntimeProvider runtime={runtime}>
            <ChatThreadUI
                conversationId={conversationId}
                inputValue={inputValue}
                setInputValue={setInputValue}
                showSuggestions={showSuggestions}
                setShowSuggestions={setShowSuggestions}
                filteredItems={filteredItems}
                setFilteredItems={setFilteredItems}
                triggerChar={triggerChar}
                setTriggerChar={setTriggerChar}
                query={query}
                setQuery={setQuery}
                offers={offers}
            />
        </AssistantRuntimeProvider>
    );
};

export default ActiveChatMobileSession;
